import { apiClient } from './client'
import type {
  Philosophy,
  PhilosophyCreate,
  PhilosophyUpdate,
  Thesis,
  ThesisCreate,
  ThesisUpdate,
  Rule,
  RuleCreate,
  RuleUpdate,
  RuleSuggestionsResponse,
  HoldingThesisLink,
  ThesisAllocationsResponse,
  ThesisHealthScoresResponse,
} from '../types/thesis'

const BASE_PATH = '/api/thesis'

// Seed example data response type
export interface SeedExamplesResponse {
  message: string
  philosophy: string
  theses: number
  rules: number
}

// Seed examples API
export const seedExamplesApi = {
  seed: async (): Promise<SeedExamplesResponse> => {
    const response = await apiClient.post<SeedExamplesResponse>(`${BASE_PATH}/seed-examples`)
    return response.data
  },
}

// Allocations API
export const allocationsApi = {
  get: async (portfolio_id?: string | null): Promise<ThesisAllocationsResponse> => {
    const params = portfolio_id ? { portfolio_id } : {}
    const response = await apiClient.get<ThesisAllocationsResponse>(
      `${BASE_PATH}/allocations`,
      { params }
    )
    return response.data
  },
}

// Health Scores API
export const healthScoresApi = {
  get: async (portfolio_id?: string | null): Promise<ThesisHealthScoresResponse> => {
    const params = portfolio_id ? { portfolio_id } : {}
    const response = await apiClient.get<ThesisHealthScoresResponse>(
      `${BASE_PATH}/health-scores`,
      { params }
    )
    return response.data
  },
}

// Philosophy API
export const philosophyApi = {
  list: async (): Promise<Philosophy[]> => {
    const response = await apiClient.get<Philosophy[]>(`${BASE_PATH}/philosophies`)
    return response.data
  },

  get: async (id: string): Promise<Philosophy> => {
    const response = await apiClient.get<Philosophy>(`${BASE_PATH}/philosophies/${id}`)
    return response.data
  },

  create: async (data: PhilosophyCreate): Promise<Philosophy> => {
    const response = await apiClient.post<Philosophy>(`${BASE_PATH}/philosophies`, data)
    return response.data
  },

  update: async (id: string, data: PhilosophyUpdate): Promise<Philosophy> => {
    const response = await apiClient.put<Philosophy>(`${BASE_PATH}/philosophies/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/philosophies/${id}`)
  },
}

// Thesis API
export const thesisApi = {
  list: async (params?: { philosophy_id?: string; status?: string }): Promise<Thesis[]> => {
    const response = await apiClient.get<Thesis[]>(`${BASE_PATH}/theses`, { params })
    return response.data
  },

  get: async (id: string): Promise<Thesis> => {
    const response = await apiClient.get<Thesis>(`${BASE_PATH}/theses/${id}`)
    return response.data
  },

  create: async (data: ThesisCreate): Promise<Thesis> => {
    const response = await apiClient.post<Thesis>(`${BASE_PATH}/theses`, data)
    return response.data
  },

  update: async (id: string, data: ThesisUpdate): Promise<Thesis> => {
    const response = await apiClient.put<Thesis>(`${BASE_PATH}/theses/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/theses/${id}`)
  },

  suggestRules: async (id: string): Promise<RuleSuggestionsResponse> => {
    const response = await apiClient.post<RuleSuggestionsResponse>(
      `${BASE_PATH}/theses/${id}/suggest-rules`
    )
    return response.data
  },

  getHoldings: async (id: string): Promise<unknown[]> => {
    const response = await apiClient.get(`${BASE_PATH}/theses/${id}/holdings`)
    return response.data
  },
}

// Rule API
export const ruleApi = {
  list: async (params?: {
    thesis_id?: string
    enabled?: boolean
    condition_type?: string
  }): Promise<Rule[]> => {
    const response = await apiClient.get<Rule[]>(`${BASE_PATH}/rules`, { params })
    return response.data
  },

  get: async (id: string): Promise<Rule> => {
    const response = await apiClient.get<Rule>(`${BASE_PATH}/rules/${id}`)
    return response.data
  },

  create: async (data: RuleCreate): Promise<Rule> => {
    const response = await apiClient.post<Rule>(`${BASE_PATH}/rules`, data)
    return response.data
  },

  update: async (id: string, data: RuleUpdate): Promise<Rule> => {
    const response = await apiClient.put<Rule>(`${BASE_PATH}/rules/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/rules/${id}`)
  },
}

// Holding-Thesis linking API
export const holdingThesisApi = {
  getHoldingTheses: async (holdingId: string): Promise<Thesis[]> => {
    const response = await apiClient.get<Thesis[]>(`${BASE_PATH}/holdings/${holdingId}/theses`)
    return response.data
  },

  setHoldingTheses: async (holdingId: string, data: HoldingThesisLink): Promise<void> => {
    await apiClient.put(`${BASE_PATH}/holdings/${holdingId}/theses`, data)
  },

  linkHoldingToThesis: async (holdingId: string, thesisId: string): Promise<void> => {
    await apiClient.post(`${BASE_PATH}/holdings/${holdingId}/theses/${thesisId}`)
  },

  unlinkHoldingFromThesis: async (holdingId: string, thesisId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/holdings/${holdingId}/theses/${thesisId}`)
  },
}
